import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  message,
  Typography,
  Form,
  Input,
  Spin,
  Rate,
  Upload,
  Modal,
  Space,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import VideoRecorder from "../../../components/features/VideoRecorder";
import { guestLinkService, guestSubmissionService } from "../../../services";

const { Title, Text } = Typography;

const SubmitTestimonial = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [uploadedVideoPreview, setUploadedVideoPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [guestLinkInfo, setGuestLinkInfo] = useState(null);
  const [isValidToken, setIsValidToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [form] = Form.useForm();
  const ratingValue = Form.useWatch("rating", form) || 0;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth <= 1024 && window.innerWidth > 768,
  );
  const [uploadError, setUploadError] = useState(null);

  const maxDuration = 180; //3 minutes
  const videoPreviewRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Guest link validation
  useEffect(() => {
    const validateGuestToken = async () => {
      try {
        setIsLoading(true);
        const response = await guestLinkService.validateGuestToken(token);

        if (response.status) {
          setGuestLinkInfo(response.data);
          setIsValidToken(true);
          message.success("Guest link validated successfully!");
        } else {
          setIsValidToken(false);
          message.error(
            response.message || "This guest link is invalid or has expired.",
          );
        }
      } catch (error) {
        setIsValidToken(false);
        message.error(error.message || "Failed to validate guest link.");
      } finally {
        setIsLoading(false);
      }
    };

    if (token) validateGuestToken();
    else {
      setIsValidToken(false);
      setIsLoading(false);
    }
  }, [token]);

  // Cleanup preview URL when component unmounts or when uploaded video changes
  useEffect(() => {
    return () => {
      if (uploadedVideoPreview) {
        URL.revokeObjectURL(uploadedVideoPreview);
      }
    };
  }, [uploadedVideoPreview]);

  const handleRecordingComplete = (videoData) => {
    setRecordedVideo(videoData);
    setUploadedVideo(null); // Clear uploaded video if exists
    setUploadedVideoPreview(null); // Clear uploaded video preview
    setCurrentStep(2);
    message.success("Recording completed successfully!");
  };

  const handleUploadComplete = (file) => {
    const previewUrl = URL.createObjectURL(file);

    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      if (video.duration > maxDuration) {
        URL.revokeObjectURL(previewUrl);
        URL.revokeObjectURL(video.src);

        message.error(
          `Video duration (${formatDuration(video.duration)}) exceeds the maximum allowed length of ${formatDuration(maxDuration)}. Please upload a shorter video.`,
          5,
        );
        return;
      }

      URL.revokeObjectURL(video.src);
      const videoData = {
        file: file,
        name: file.name,
        type: file.type,
        size: file.size,
        duration: Math.round(video.duration),
        previewUrl: previewUrl,
      };

      setUploadedVideo(videoData);
      setRecordedVideo(null);
      setUploadedVideoPreview(previewUrl);
      setCurrentStep(2);
      setIsUploadModalVisible(false); // Only close modal on successful upload
      message.success("Video uploaded successfully!");
    };

    video.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      URL.revokeObjectURL(video.src);
      message.error("Failed to load video metadata. Please try another file.");
    };

    video.src = URL.createObjectURL(file);
  };

  const handleRemoveUploadedVideo = () => {
    if (uploadedVideoPreview) {
      URL.revokeObjectURL(uploadedVideoPreview);
    }
    setUploadedVideo(null);
    setUploadedVideoPreview(null);
    message.info("Video removed");
  };

  const uploadVideoToServer = async () => {
    const videoToUpload = recordedVideo || uploadedVideo;

    if (!videoToUpload) {
      message.error("Please record or upload a video first!");
      return { success: false };
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let videoFile;
      if (recordedVideo) {
        videoFile = new File(
          [recordedVideo.blob],
          `guest-video-${token}-${Date.now()}.webm`,
          { type: "video/webm" },
        );
      } else {
        videoFile = uploadedVideo.file;
      }

      const uploadResponse = await guestSubmissionService.uploadGuestVideo(
        videoFile,
        (progress) => setUploadProgress(progress),
      );

      if (uploadResponse?.status && uploadResponse?.data?.fileUrl) {
        const baseUrl = process.env.REACT_APP_API_URL;
        const fullVideoUrl = `${baseUrl}${uploadResponse?.data?.fileUrl}`;
        return {
          success: true,
          videoUrl: fullVideoUrl,
          filename: uploadResponse.data.filename,
          duration: videoToUpload.duration || 0,
          fileSize: uploadResponse.data.fileSize,
        };
      }

      throw new Error("Upload failed");
    } catch (error) {
      message.error(error.message || "Upload failed");
      return { success: false };
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setIsUploading(true);
      const uploadResult = await uploadVideoToServer();
      if (!uploadResult.success) throw new Error("Upload failed");

      const submissionData = {
        guestName: values.name,
        guestEmail: values.email,
        mobileNumber: values.mobileNumber,
        companyId: guestLinkInfo?.companyId,
        videoUrl: uploadResult.videoUrl,
        videoDuration: uploadResult.duration,
        fileSize: uploadResult.fileSize,
        filename: uploadResult.filename,
        rating: values.rating,
      };

      const response = await guestSubmissionService.createGuestSubmission(
        token,
        submissionData,
      );

      if (response.status) {
        message.success("Testimonial submitted successfully!");
        setCurrentStep(3);
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      message.error(error.message || "Submission failed");
    } finally {
      setIsUploading(false);
    }
  };

  const getPadding = () => {
    if (isMobile) return "12px";
    if (isTablet) return "20px";
    return "24px";
  };

  const getCardPadding = () => {
    if (isMobile) return "16px";
    return "24px";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  /* LOADING */
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }

  /* INVALID TOKEN */
  if (!isValidToken) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: getPadding(),
        }}
      >
        <Card
          style={{
            maxWidth: 420,
            textAlign: "center",
            width: "100%",
            padding: isMobile ? "20px" : "24px",
          }}
        >
          <Title level={isMobile ? 4 : 3}>Invalid Access</Title>
          <Text>This guest link is invalid or expired.</Text>
        </Card>
      </div>
    );
  }

  /* SUCCESS */
  if (currentStep === 3) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        style={{ padding: getPadding() }}
      >
        <Card
          className="text-center rounded-xl shadow-md"
          style={{
            width: "100%",
            maxWidth: 500,
            padding: isMobile ? "24px" : "32px",
          }}
        >
          <div
            style={{ fontSize: isMobile ? "3rem" : "4rem", marginBottom: 16 }}
          >
            🎉
          </div>
          <Title level={isMobile ? 4 : 3}>Thank You!</Title>
          <Text
            type="secondary"
            style={{
              display: "block",
              marginBottom: 24,
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            Your testimonial has been successfully submitted.
            <br />
            Our team will review it shortly.
          </Text>

          <Button
            type="primary"
            onClick={() => navigate("/")}
            size={isMobile ? "middle" : "large"}
          >
            ← Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: getPadding(),
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          width: "100%",
        }}
      >
        {/* Header with Upload Button */}
        {/* <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isMobile ? 16 : 24,
          }}
        >
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            Share Your Testimonial
          </Title>

          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={() => setIsUploadModalVisible(true)}
            size={isMobile ? "middle" : "large"}
            style={{
              borderRadius: 999,
              borderColor: "#4f46e5",
              color: "#4f46e5",
            }}
          >
            {isMobile ? "Upload" : "Upload Video"}
          </Button>
        </div> */}

        {/* RIGHT COLUMN - Main Content */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 16 : 24,
          }}
        >
          {/* VIDEO RECORDING CARD */}
          {!uploadedVideo && (
            <Card
              style={{
                borderRadius: 16,
                padding: getCardPadding(),
                width: "100%",
              }}
            >
              <Title level={isMobile ? 5 : 4} style={{ marginBottom: 4 }}>
                Record Your Testimonial
              </Title>

              <Text
                type="secondary"
                style={{ fontSize: isMobile ? "13px" : "14px" }}
              >
                Share your story in your own words
              </Text>

              <div style={{ marginTop: 20, width: "100%" }}>
                <VideoRecorder
                  onRecordingComplete={handleRecordingComplete}
                  maxDuration={180}
                  showPreview
                  isMobile={isMobile}
                />
              </div>
              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                }}
              >
                <Text type="secondary" style={{ fontWeight: 500 }}>
                  — OR —
                </Text>

                <Button
                  type="default"
                  icon={<UploadOutlined />}
                  onClick={() => setIsUploadModalVisible(true)}
                  size={isMobile ? "middle" : "large"}
                  style={{
                    borderRadius: 999,
                    borderColor: "#4f46e5",
                    color: "#4f46e5",
                    minWidth: isMobile ? 160 : 200,
                  }}
                >
                  {isMobile ? "Upload" : "Upload Video"}
                </Button>
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#64748b",
                  fontSize: isMobile ? "12px" : "14px",
                  flexWrap: "wrap",
                }}
              >
                <InfoCircleOutlined />
                Maximum recording length: 3 minutes
              </div>
            </Card>
          )}
          {/* UPLOADED VIDEO PREVIEW CARD (shown when video is uploaded) */}
          {uploadedVideo && (
            <Card
              style={{
                borderRadius: 16,
                padding: getCardPadding(),
                width: "100%",
                background: "#f0f9ff",
                border: "1px solid #bae6fd",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Space>
                  <CheckCircleOutlined
                    style={{ color: "#059669", fontSize: 20 }}
                  />
                  <Title
                    level={isMobile ? 5 : 4}
                    style={{ margin: 0, color: "#065f46" }}
                  >
                    Uploaded Video
                  </Title>
                </Space>
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={handleRemoveUploadedVideo}
                >
                  Remove
                </Button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 16,
                  alignItems: isMobile ? "stretch" : "center",
                }}
              >
                {/* Video Thumbnail/Preview */}
                <div
                  style={{
                    width: isMobile ? "100%" : 200,
                    height: isMobile ? 150 : 120,
                    background: "#000",
                    borderRadius: 8,
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onClick={() => setIsPreviewModalVisible(true)}
                >
                  <video
                    src={uploadedVideoPreview}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      background: "rgba(0,0,0,0.6)",
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlayCircleOutlined
                      style={{ color: "#fff", fontSize: 24 }}
                    />
                  </div>
                </div>

                {/* Video Info */}
                <div style={{ flex: 1 }}>
                  <Text
                    strong
                    style={{ fontSize: 16, display: "block", marginBottom: 8 }}
                  >
                    {uploadedVideo.name}
                  </Text>
                  <Space wrap size={[16, 8]}>
                    <Text type="secondary">
                      📁 {formatFileSize(uploadedVideo.size)}
                    </Text>
                    <Text type="secondary">
                      ⏱️ {formatDuration(uploadedVideo.duration)}
                    </Text>
                    <Text type="secondary">
                      🎬 {uploadedVideo.type.split("/")[1]?.toUpperCase()}
                    </Text>
                  </Space>
                </div>
              </div>
            </Card>
          )}

          {/* FORM CARD */}
          <Card
            style={{
              borderRadius: 16,
              padding: isMobile ? "16px" : "24px",
              width: "100%",
            }}
          >
            <Title
              level={isMobile ? 5 : 4}
              style={{
                marginBottom: 6,
                fontWeight: 600,
                color: "#0f172a",
              }}
            >
              Share Your Story
            </Title>

            <Text
              style={{
                color: "#64748b",
                fontSize: isMobile ? "13px" : "14px",
                display: "block",
                marginBottom: 20,
              }}
            >
              Help us understand your experience by completing the form below
            </Text>

            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              initialValues={{ rating: 5 }}
            >
              {/* FULL NAME */}
              <Form.Item
                name="name"
                label={
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: isMobile ? "13px" : "14px",
                    }}
                  >
                    Full Name <span style={{ color: "red" }}>*</span>
                  </span>
                }
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input
                  placeholder="Enter your full name"
                  prefix={<UserOutlined />}
                  style={{
                    height: isMobile ? 40 : 44,
                    borderRadius: 999,
                    paddingLeft: 12,
                    fontSize: isMobile ? "14px" : "16px",
                  }}
                  size={isMobile ? "small" : "middle"}
                />
              </Form.Item>

              {/* EMAIL + MOBILE */}
              <div
                style={{
                  display: "flex",
                  gap: isMobile ? 12 : 14,
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <Form.Item
                  name="email"
                  label={
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: isMobile ? "13px" : "14px",
                      }}
                    >
                      Email Address <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                  style={{ flex: 1, marginBottom: isMobile ? 0 : "unset" }}
                >
                  <Input
                    placeholder="Enter your email address"
                    prefix={<MailOutlined />}
                    style={{
                      height: isMobile ? 40 : 44,
                      borderRadius: 999,
                      paddingLeft: 12,
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                    size={isMobile ? "small" : "middle"}
                  />
                </Form.Item>

                <Form.Item
                  name="mobileNumber"
                  label={
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: isMobile ? "13px" : "14px",
                      }}
                    >
                      Mobile Number <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please enter your mobile number",
                    },
                  ]}
                  style={{ flex: 1, marginBottom: isMobile ? 0 : "unset" }}
                >
                  <Input
                    placeholder="Enter Your Mobile Number"
                    style={{
                      height: isMobile ? 40 : 44,
                      borderRadius: 999,
                      paddingLeft: 12,
                      fontSize: isMobile ? "14px" : "16px",
                    }}
                    size={isMobile ? "small" : "middle"}
                  />
                </Form.Item>
              </div>

              {/* REVIEWS & RATINGS */}
              <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                <Text
                  style={{
                    fontWeight: 500,
                    display: "block",
                    marginBottom: 4,
                    fontSize: isMobile ? "13px" : "14px",
                  }}
                >
                  Reviews & Ratings
                </Text>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: isMobile ? "wrap" : "nowrap",
                    gap: isMobile ? 8 : 0,
                  }}
                >
                  <Text
                    style={{
                      color: "#64748b",
                      fontSize: isMobile ? "12px" : "13px",
                      flexShrink: 0,
                    }}
                  >
                    Click stars to rate ({ratingValue}/5)
                  </Text>

                  <Form.Item name="rating" noStyle>
                    <Rate style={{ fontSize: isMobile ? 16 : 20 }} />
                  </Form.Item>
                </div>
              </div>

              {/* Video Status Indicator */}
              {/* {(recordedVideo || uploadedVideo) && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "12px 16px",
                    background: "#f0f9ff",
                    borderRadius: 8,
                    border: "1px solid #bae6fd",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <CheckCircleOutlined style={{ color: "#059669" }} />
                  <Text style={{ color: "#065f46" }}>
                    {recordedVideo
                      ? "✅ Video recorded successfully"
                      : "✅ Video uploaded successfully"}
                  </Text>
                </div>
              )} */}

              {/* SUBMIT BUTTON */}
              <Button
                type="primary"
                htmlType="submit"
                loading={isUploading}
                block
                size={isMobile ? "middle" : "large"}
                style={{
                  height: isMobile ? 44 : 48,
                  borderRadius: 999,
                  background: "#4f46e5",
                  fontWeight: 500,
                  fontSize: isMobile ? "14px" : "15px",
                }}
                disabled={!recordedVideo && !uploadedVideo}
              >
                {isUploading ? "Submitting..." : "Submit →"}
              </Button>

              {!recordedVideo && !uploadedVideo && (
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    marginTop: 8,
                    textAlign: "center",
                  }}
                >
                  Please record or upload a video to submit
                </Text>
              )}
            </Form>
          </Card>
        </div>
      </div>

      {/* Upload Video Modal */}
      <Modal
        title="Upload Video"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setUploadError(null); // Clear error when closing
        }}
        footer={null}
        width={isMobile ? "95%" : 600}
      >
        <div style={{ padding: isMobile ? "12px" : "20px" }}>
          <Upload.Dragger
            name="file"
            multiple={false}
            accept="video/mp4,video/webm,video/quicktime"
            showUploadList={false}
            beforeUpload={(file) => {
              // Clear previous error
              setUploadError(null);

              const isVideo = file.type.startsWith("video/");
              if (!isVideo) {
                setUploadError("You can only upload video files!");
                return Upload.LIST_IGNORE;
              }

              const isLessThan100MB = file.size / 1024 / 1024 < 100;
              if (!isLessThan100MB) {
                setUploadError("Video must be smaller than 100MB!");
                return Upload.LIST_IGNORE;
              }

              // Check video duration
              const video = document.createElement("video");
              video.preload = "metadata";

              video.onloadedmetadata = () => {
                // Check if video duration exceeds max duration
                if (video.duration > maxDuration) {
                  // Clean up
                  URL.revokeObjectURL(video.src);

                  // Set error message
                  setUploadError(
                    `Video duration (${formatDuration(video.duration)}) exceeds the maximum allowed length of ${formatDuration(maxDuration)}. Please upload a shorter video.`,
                  );

                  // Don't close the modal, just return
                  return;
                }

                // If duration is valid, proceed with upload
                URL.revokeObjectURL(video.src);
                handleUploadComplete(file);
              };

              video.onerror = () => {
                URL.revokeObjectURL(video.src);
                setUploadError(
                  "Could not read video file. Please try another.",
                );
              };

              video.src = URL.createObjectURL(file);
              return false; // Prevent automatic upload
            }}
          >
            <div style={{ padding: isMobile ? "20px" : "30px" }}>
              <UploadOutlined
                style={{ fontSize: 40, color: "#4f46e5", marginBottom: 16 }}
              />
              <Title level={isMobile ? 5 : 4} style={{ margin: "8px 0" }}>
                Click or drag video to upload
              </Title>
              <Text type="secondary">
                Support: MP4, WebM, MOV (Max 100MB, max{" "}
                {formatDuration(maxDuration)} duration)
              </Text>
            </div>
          </Upload.Dragger>

          {/* Error Message Display */}
          {uploadError && (
            <div
              style={{
                marginTop: 16,
                padding: "12px 16px",
                background: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 8,
                color: "#f5222d",
                fontSize: isMobile ? "13px" : "14px",
                textAlign: "center",
              }}
            >
              <CloseCircleOutlined
                style={{ marginRight: 8, color: "#f5222d" }}
              />
              {uploadError}
            </div>
          )}

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              By uploading, you confirm you have the right to share this video
            </Text>
          </div>
        </div>
      </Modal>

      {/* Video Preview Modal */}
      <Modal
        title="Video Preview"
        open={isPreviewModalVisible}
        onCancel={() => {
          // Pause and clean up video when modal closes
          if (videoPreviewRef.current) {
            videoPreviewRef.current.pause();
            videoPreviewRef.current.currentTime = 0; // Reset to beginning
          }
          setIsPreviewModalVisible(false);
        }}
        afterClose={() => {
          // Additional cleanup after modal is fully closed
          if (videoPreviewRef.current) {
            videoPreviewRef.current.pause();
            videoPreviewRef.current.currentTime = 0;
            videoPreviewRef.current.removeAttribute("src"); // Remove src to stop loading
            videoPreviewRef.current.load(); // Reset the video element
          }
        }}
        footer={null}
        width={isMobile ? "95%" : 800}
        centered
        destroyOnClose={true} // This ensures the video element is recreated when modal opens
      >
        <div style={{ padding: isMobile ? "12px" : "20px" }}>
          {uploadedVideo && (
            <div>
              <video
                ref={videoPreviewRef}
                src={uploadedVideoPreview}
                controls
                autoPlay
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  background: "#000",
                  borderRadius: 8,
                }}
                onPause={() => {
                  // Optional: Track when user pauses video
                }}
              />
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <Text strong>{uploadedVideo.name}</Text>
                <br />
                <Text type="secondary">
                  {formatFileSize(uploadedVideo.size)} •{" "}
                  {formatDuration(uploadedVideo.duration)}
                </Text>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SubmitTestimonial;
